import { Point, Polygon, ISiteWalkMediaLevel } from './types';
import { ZONE_COLORS } from './constants';

// ── Geometry ─────────────────────────────────────────────────────────────────

export const distance = (a: Point, b: Point) => Math.sqrt((a.x - b.x) ** 2 + (a.y - b.y) ** 2);

/** Ray-casting point-in-polygon test */
export const isPointInsidePolygon = (point: Point, polygon: Polygon): boolean => {
    let inside = false;
    const pts = polygon.points;

    for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
        const crossesHorizon = pts[i].y > point.y !== pts[j].y > point.y;
        const isLeftOfEdge =
            point.x < ((pts[j].x - pts[i].x) * (point.y - pts[i].y)) / (pts[j].y - pts[i].y) + pts[i].x;
        if (crossesHorizon && isLeftOfEdge) inside = !inside;
    }

    return inside;
};

// ── Coordinate conversion ─────────────────────────────────────────────────────

/** Convert normalized (0–1) points → canvas pixel coordinates */
export const toCanvasPoints = (normalizedPoints: Point[], canvasWidth: number, canvasHeight: number): Point[] =>
    normalizedPoints.map((p) => ({
        x: p.x * canvasWidth,
        y: p.y * canvasHeight,
    }));

/** Convert canvas pixel coordinates → normalized (0–1) points */
export const toNormalizedPoints = (points: Point[], canvasWidth: number, canvasHeight: number): Point[] =>
    points.map((p) => ({
        x: p.x / canvasWidth,
        y: p.y / canvasHeight,
    }));

// ── Color ─────────────────────────────────────────────────────────────────────

/** Pick the next color in the rotation based on how many zones already exist */
export const getNextZoneColor = (existingPolygons: Polygon[]) =>
    ZONE_COLORS[existingPolygons.length % ZONE_COLORS.length];

/** Convert a CSS hex color to rgba() with a given opacity */
export const hexToRgba = (hex: string, alpha: number) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r},${g},${b},${alpha})`;
};

// ── Data conversion ───────────────────────────────────────────────────────────

/** Build a Polygon[] from the API-level ISiteWalkMediaLevel[] */
export const levelsToPolygons = (levels: ISiteWalkMediaLevel[], canvasWidth: number, canvasHeight: number): Polygon[] =>
    levels?.map((level) => ({
        id: level.floorPlan,
        floorPlan: level.floorPlan,
        points: toCanvasPoints(level.points, canvasWidth, canvasHeight),
        label: level.floorPlan,
        color: level.color || ZONE_COLORS[0],
    }));

export const getMediaType = (file: File) => {
    return file.type.startsWith('video/') ? 'VIDEO' : 'IMAGE';
};
